import MUIButton, { type ButtonProps as MUIButtonProps } from '@mui/material/Button';
import { AnchorHTMLAttributes, MouseEvent, useEffect } from 'react';
import { Link } from 'react-router';
import { useTracking, UseTrackingOptions } from '../utils/application-utils/use-tracking';
import { ButtonColor, ButtonVariant, buttonVariants, sizes, Sizes } from './button-constants';

type BaseButtonProps = Omit<MUIButtonProps, 'variant' | 'color' | 'size'> &
  AnchorHTMLAttributes<HTMLAnchorElement>;

export interface ButtonProps
  extends BaseButtonProps, Pick<UseTrackingOptions, 'trackingEnabled' | 'trackingData'> {
  variant?: ButtonVariant;
  color?: ButtonColor | 'inherit';
  size?: Sizes;
  to?: string;
  fullHeight?: boolean;
}

export const Button = ({
  trackingEnabled = true,
  trackingData,
  children,
  onClick,
  sx,
  to,
  size,
  variant,
  component,
  fullHeight,
  color = 'primary',
  'aria-label': ariaLabel,
  ...props
}: ButtonProps) => {
  const { trackEvent } = useTracking({ trackingEnabled, trackingData, ariaLabel, children });

  const hasLinkDestination = Boolean(to);
  const buttonVariant = getButtonVariant(hasLinkDestination, variant);
  const componentToUse = component || (hasLinkDestination ? Link : undefined);

  const onClickTrackingInjection = (e: MouseEvent<HTMLButtonElement>) => {
    if (trackingEnabled) {
      const type = !props.type || props.type === 'button' ? 'click' : props.type;
      trackEvent('button', type, typeof children === 'string' ? children : undefined);
    }
    onClick?.(e);
  };

  const sizeInvalid = size && !sizes[size];
  const resolvedSize = sizeInvalid ? 'medium' : (size ?? 'medium');

  useEffect(() => {
    if (sizeInvalid) console.warn(`Invalid button size. Defaulting to "medium".`);
  }, [sizeInvalid]);

  const mergedSx = getStyle({ sx, size: resolvedSize, fullHeight });

  return (
    <MUIButton
      {...props}
      color={color}
      sx={mergedSx}
      aria-label={ariaLabel}
      variant={buttonVariant}
      onClick={onClickTrackingInjection}
      size={resolvedSize === 'xsmall' ? 'small' : resolvedSize}
      {...(componentToUse && { component: componentToUse })}
      {...(hasLinkDestination && { to })}
    >
      {children}
    </MUIButton>
  );
};

const getStyle = ({ sx, size = 'medium', fullHeight }: ButtonProps) => {
  const passedStyles = Array.isArray(sx) ? sx : [sx || {}];
  const buttonSize = sizes[size];
  const heightRestraint = fullHeight ? { minHeight: buttonSize } : { height: buttonSize };
  return [heightRestraint, ...passedStyles];
};

const isSupportedVariant = (v: ButtonVariant): v is ButtonVariant => buttonVariants.includes(v);

const getButtonVariant = (preferTextVariant = false, variant?: ButtonVariant): ButtonVariant => {
  if (variant && isSupportedVariant(variant)) return variant;
  // An unexpected variant was provided, log a warning and fall back to default
  if (variant) console.warn(`Unsupported button variant "${variant}" provided, using default`);
  return preferTextVariant ? 'text' : 'primary';
};
